import IconPlus from "assets/img/icon-plus-16.svg?react"

import React, { FunctionComponent, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import cn from "classnames"

import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import AssetStatusTag from "components/AssetStatusTag/AssetStatusTag"
import {
    ICopyAssetState,
    effectiveValue,
    rowErrors,
} from "./copyAssetRows"

export interface ICopyAssetRowListProps {
    state: ICopyAssetState
    canAdd: boolean
    onSelect(id: string): void
    onAdd(): void
}

/**
 * Master list of the copy dialog: one entry per copy with its number, LMID (or
 * how it will be obtained), status, and markers for own values and missing
 * input. Selecting an entry scrolls its card into view.
 */
const CopyAssetRowList: FunctionComponent<React.PropsWithChildren<ICopyAssetRowListProps>> = ({
    state,
    canAdd,
    onSelect,
    onAdd,
}) => {
    const { t } = useTranslation()
    const itemsRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const container = itemsRef.current
        const item = container?.querySelector<HTMLElement>(`[data-row-id="${state.activeRowId}"]`)
        if (!container || !item) return

        const itemTop = item.offsetTop
        const itemBottom = itemTop + item.offsetHeight
        if (itemTop < container.scrollTop) {
            container.scrollTop = Math.max(0, itemTop - 4)
        } else if (itemBottom > container.scrollTop + container.clientHeight) {
            container.scrollTop = itemBottom - container.clientHeight + 4
        }
    }, [state.activeRowId])

    function rowTitle(rowIndex: number): string {
        const row = state.rows[rowIndex]
        if (row.lmid) return row.lmid
        return t(`lmidModal.${row.lmidMode}`)
    }

    return (
        <div className="copy-asset__list border-grey-100 bg-white d-flex" data-testid="copy-asset-list">
            <Flex justifyContent="space-between" alignItems="center" gap="0.8rem" className="px-2 py-1 bg-grey-50 border-bottom-grey-200">
                <Text type="p" className="font-bold">
                    {t('copyAsset.copies', { count: state.rows.length })}
                </Text>
                <Button
                    round
                    theme="grey"
                    ariaLabel={t('copyAsset.addCopy')}
                    title={t('copyAsset.addCopy')}
                    icon={<IconPlus />}
                    disabled={!canAdd}
                    onClick={onAdd}
                />
            </Flex>
            <div ref={itemsRef} className="copy-asset__list-items">
                {state.rows.map((row, index) => {
                    const isSelected = row.id === state.activeRowId
                    const hasErrors = rowErrors(state, row).length > 0
                    const overridesCount = Object.keys(row.overrides).length
                    return (
                        <button
                            type="button"
                            key={row.id}
                            className={cn("copy-asset__list-item w-100 text-left", { "copy-asset__list-item--selected": isSelected })}
                            onClick={() => onSelect(row.id)}
                            data-row-id={row.id}
                            data-testid={`copy-asset-list-item-${index + 1}`}>
                            <Flex alignItems="center" gap="0.8rem">
                                <span className={cn("copy-asset__number", { "copy-asset__number--selected": isSelected })}>
                                    {index + 1}
                                </span>
                                <div className="w-100 min-w-0">
                                    <Flex justifyContent="space-between" alignItems="center" gap="0.8rem">
                                        <Text type="p" className="font-bold">{rowTitle(index)}</Text>
                                        <Flex alignItems="center" gap="0.4rem" className="flex-shrink-0">
                                            {overridesCount > 0 &&
                                                <Text type="pSmallest" className="copy-asset__edited-chip px-1 round-sm">
                                                    {t('copyAsset.edited', { count: overridesCount })}
                                                </Text>
                                            }
                                            {hasErrors &&
                                                <span className="copy-asset__error-dot" title={t('copyAsset.needsInput')}>!</span>
                                            }
                                        </Flex>
                                    </Flex>
                                    <Flex alignItems="center" gap="0.8rem">
                                        <AssetStatusTag status={effectiveValue(state, row, "status")} />
                                        {!!row.serialNumber &&
                                            <Text type="pSmallest" className="color-grey-500">
                                                {t('copyAsset.serialShort')}: {row.serialNumber}
                                            </Text>
                                        }
                                    </Flex>
                                </div>
                            </Flex>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default CopyAssetRowList
